import React, { useEffect, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";

import { getFlag } from '../../Helpers/FlagHandler';
import { useTranslate } from '@tolgee/react';
import { ReactComponent as Search } from "../../Assets/iconsv2/generic/search.svg"
import { ReactComponent as MicIcon } from "../../Assets/iconsv2/settings/microphone.svg"

const LanguageListSelector = ({ 
    isLanguageSelectorOpen, 
    openLanguageList, 
    valuesList, 
    setValue, 
    callLanguage, 
    selectedValue 
}) => {
    const { t } = useTranslate();
    const theme = useTheme();

    const [searchKey, setSearchKey] = useState("");
    const [localValue, setLocalValue] = useState(selectedValue);
    const [focusedIndex, setFocusedIndex] = useState(0); 

    const modalRef = useRef(null);
    const searchInputRef = useRef(null); 
    const activeItemRef = useRef(null); 

    const checkSelected = (item) => {
        if (callLanguage) {
            if (item.locale === localValue) {
                return true
            } else {
                return false
            }
        } else {
            if (item.name === localValue) {
                return true
            } else {
                return false
            }
        }
    }

    const filter = (list, key) => {
        if (key === "") {
            return list
        }
        let found = list.filter(item => 
            item.name.toLowerCase().includes(key.toLowerCase()) || 
            item.translation.toLowerCase().includes(key.toLowerCase())
        );
        return found;
    }

    const filteredValues = filter(valuesList, searchKey);

    const handleOnClick = (item) => {
        const newValue = callLanguage ? item.locale : item.name;
        setLocalValue(newValue);
        setValue(newValue);
        openLanguageList();
    }

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault(); 
            setFocusedIndex(prev => (prev < filteredValues.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredValues.length > 0 && focusedIndex >= 0) {
                handleOnClick(filteredValues[focusedIndex]);
            }
        }
    };

    useEffect(() => {
        if (activeItemRef.current) {
            activeItemRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [focusedIndex]);

    useEffect(() => {
        setFocusedIndex(0);
    }, [searchKey]);

    useEffect(() => {
        if (isLanguageSelectorOpen && searchInputRef.current) {
            requestAnimationFrame(() => {
                searchInputRef.current?.focus();
            });
        } else if (!isLanguageSelectorOpen) {
            setSearchKey(""); 
        }
    }, [isLanguageSelectorOpen]);

    useEffect(() => {
        const keyDownHandler = event => {
            if (event.key === 'Escape') {
                event.preventDefault();
                openLanguageList();
            }
        };

        document.addEventListener('keydown', keyDownHandler);

        return () => {
            document.removeEventListener('keydown', keyDownHandler);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                openLanguageList();
            }
        };

        if (isLanguageSelectorOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isLanguageSelectorOpen, openLanguageList]);

    return (
        <FullScreenItem>
            <Container ref={modalRef}>
                <Header>
                    <Title>
                        {t('settings.language_picker')}
                    </Title>
                    <CloseBtn>
                        <CloseIcon onClick={openLanguageList} />
                    </CloseBtn>
                </Header>
                <SearchWrapper>
                    <Search />
                    <SearchInput
                        ref={searchInputRef}
                        value={searchKey}
                        onChange={(e) => setSearchKey(e.currentTarget.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('general.search')}
                    />
                </SearchWrapper>
                <ListContainer>
                    {filteredValues?.map((item, index) => {
                        const isSelected = checkSelected(item);
                        const isFocused = focusedIndex === index;

                        return (
                            <ListItem
                                key={index}
                                selected={isSelected}
                                $isFocused={isFocused}
                                ref={isFocused ? activeItemRef : null}
                                onClick={() => handleOnClick(item)}
                            >
                                <Flag src={getFlag(item.country_code)} alt={item.name} />  {item.name} ({item.translation})
                                {item.voice_cloning_supported && (
                                    <VoiceCloneBadge title={t("video_tool.voice_cloning_supported_tooltip")}>
                                        <MicIcon />
                                    </VoiceCloneBadge>
                                )}
                            </ListItem>
                        )
                    })}
                </ListContainer>
            </Container>
        </FullScreenItem>
    )
}

export default LanguageListSelector

const FullScreenItem = styled.div`
position:absolute;
top:0;
left:0;
right:0;
bottom:0;
background:rgba(0,0,0,0.3);
width:100%;
height:100%;
display:flex;
justify-content:center;
align-items:center;
z-index:2;
`
const Container = styled.div`
display:flex;
flex-direction:column;
padding:25px;
height:90%;
width:100%;
max-width:644px;
background:${({ theme }) => theme.menu.groupContentBackground};
gap:20px;
border-radius:4px;
`
const Header = styled.div`
display:flex;
align-items:center;
justify-content:space-between;
gap:10px;
`
const CloseBtn = styled.button`
display:flex;
align-items:center;
background:none;
border:none;
cursor:pointer;
svg{
width:28px;
height:28px;
color:${({ theme }) => theme.menu.selectedOpionBackground};
stroke:${({ theme }) => theme.sidebar.borderColor};
}
`

const ListContainer = styled.div`
display:flex;
flex-direction:column;
height:100%;
overflow:auto;
`

const ListItem = styled.h3`
  display:flex;
  padding:14px 16px;
  align-items:center;
  width:100%;
  
  color:${({ selected, $isFocused, theme }) => 
    selected || $isFocused ? theme.menu.primaryTextColor : theme.menu.secondaryTextColor};
    
  background:${({ selected, $isFocused, theme }) => 
    selected || $isFocused ? theme.menu.selectedOpionBackground : "transparent"};
    
  font-size:16px;
  line-height:22px;
  
  font-weight:${({ selected, $isFocused }) => selected || $isFocused ? "600" : "500"};
  
  cursor:pointer;
  gap:10px;
  border-bottom:1px solid ${({ theme }) => theme.sidebar.borderColor};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.menu.selectedOpionBackground};
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }

  &:last-child {
    border: none;
  }
`;

const Flag = styled.img`
width:20px;
height:20px;
display:flex;
object-fit:contain;
`

const VoiceCloneBadge = styled.span`
margin-left:auto;
display:flex;
align-items:center;
opacity:0.7;
flex-shrink:0;
svg{
width:16px;
height:16px;
}
`

const Title = styled.p`
font-weight: 700;
font-size: 20px;
line-height: 100%;
letter-spacing: 0.04px;
vertical-align: middle;
color:${({ theme }) => theme.menu.primaryTextColor};

`
const SearchWrapper = styled.div`
display:flex;
align-items:center;
padding:12px 8px;
border:1px solid ${({ theme }) => theme.sidebar.borderColor};
border-radius:4px;
gap:8px;
background:${({ theme }) => theme.menu.background};

`
const SearchInput = styled.input`
border:none;
background:none;
color:${({ theme }) => theme.menu.secondaryTextColor};
font-weight: 500;
font-size: 16px;
line-height: 100%;
letter-spacing: 0.04px;
vertical-align: middle;
width:100%;
outline:none;
`